import React, { useState, memo, useCallback, useEffect } from "react";
import styled from "styled-components";
import theme from "../../../assets/theme";
import COLORS from "../../../assets/Colors";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  FormLabel,
  Select,
  MenuItem,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import ActionBar from "../../common/ActionBar";
import { PrimaryCTAButton, SecondaryCTAButton } from "../../common/Buttons";
import { CustomInputField } from "../../common/FormInputs";
import { Formik } from "formik";
import useLoader from "../../../hooks/useLoader";
import { useSnackbar } from "notistack";
import { getErrorMessage } from "../../../helpers/functions";
import PartyService from "../../../services/PartyService";
import CaseService from "../../../services/CaseService";

const INK = "#293461";
const INK_SOFT = "#475569";
const MUTE = "#94A3B8";
const LINE = "#E5E9F0";
const SURFACE = "#F6F8FA";
const TEAL = "#0E7C86";
const TEAL_DARK = "#0B646C";
const TEAL_WASH = "rgba(14, 124, 134, 0.08)";
const DANGER = "#DC2626";

function PartyCodeSelect({ errors, touched, setFieldValue, values }) {
  const [partyCodes, setPartyCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    async function loadPartyCodes() {
      try {
        setLoading(true);
        const response = await PartyService.getPartyCodes();
        if (response?.length) {
          const codes = response
            .filter((item) => item.customerCode) // Only items with codes
            .map(({ customerCode }) => ({
              label: customerCode,
              value: customerCode,
              // displayName: customerName,
            }));
          setPartyCodes(codes);
        }
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error), { variant: "error" });
      } finally {
        setLoading(false);
      }
    }
    loadPartyCodes();
  }, [enqueueSnackbar]);

  const handleChange = (event) => {
    const code = event.target.value;
    setFieldValue("partyCode", code);
  };

  return (
    <SelectWrapper>
      <SelectLabel error={errors.partyCode && touched.partyCode}>
        Party code
        <RequiredStar> *</RequiredStar>
      </SelectLabel>
      <StyledSelect
        variant="outlined"
        value={values.partyCode || ""}
        onChange={handleChange}
        displayEmpty
        disabled={loading}
        error={errors.partyCode && touched.partyCode}
      >
        <MenuItem value="">
          <PlaceholderText>
            {loading ? "Loading party codes…" : "Choose a party code"}
          </PlaceholderText>
        </MenuItem>
        {partyCodes.map((pc) => (
          <MenuItem key={pc.value} value={pc.value}>
            <PartyCodeItem>
              <CodeLabel>{pc.label}</CodeLabel>
              <CodeDetail>{pc.displayName}</CodeDetail>
            </PartyCodeItem>
          </MenuItem>
        ))}
      </StyledSelect>
      {errors.partyCode && touched.partyCode && (
        <ErrorText>{errors.partyCode}</ErrorText>
      )}
    </SelectWrapper>
  );
}

const slabConfig = {
  med: {
    max: 9,
    cols: ["lowerSlab", "upperSlab", "part1Fee", "part2Fee"],
    placeholders: ["0", "0", "20000", "5"],
    labels: ["Lower Slab", "Upper Slab", "Part 1 Fee", "Part 2 Fee"],
  },
  arb: {
    max: 25,
    cols: ["lowerSlab", "upperSlab", "part1Fee", "part2Fee"],
    placeholders: ["0", "0", "1000", "5"],
    labels: ["Lower Slab", "Upper Slab", "Part 1 Fee", "Part 2 Fee"],
  },
  neg: {
    max: 5,
    cols: [
      "lowerSlab",
      "upperSlab",
      "part1Fee",
      "part2Fee",
      "adValoremUpfront",
      "successFee",
      "adValoremSuccess",
    ],
    placeholders: ["0", "0", "1000", "5", "0", "1000", "4"],
    labels: [
      "Lower Slab",
      "Upper Slab",
      "Part 1 Fee",
      "Part 2 Fee",
      "AV Upfront",
      "Success Fee",
      "AV Success",
    ],
  },
};

const TAB_LIST = [
  //   { key: "neg", label: "Negotiation" },
  //   { key: "med", label: "Mediation" },
  { key: "arb", label: "Arbitration" },
];

function makeDefaultSlab(type) {
  const cfg = slabConfig[type];
  const obj = { id: Date.now() + Math.random() };
  cfg.cols.forEach((col, i) => {
    obj[col] = cfg.placeholders[i];
  });
  return obj;
}

function buildInitialValues() {
  return {
    partyCode: "",
    mode: "append",
    CurrencyUnit: "INR",

    med_claimantShare: "0.5",
    med_respondentShare: "0.5",
    med_adValoremUpfront: "0.2",
    med_adValoremSuccess: "0.8",
    med_serviceFee: "1000",
    med_nonMonetaryBase: "60000",
    med_nonMonetaryAdvalorem: "140000",
    med_slabs: [makeDefaultSlab("med")],

    arb_claimantPercent: "1",
    arb_respondentPercent: "0",
    arb_filingFee: "10000",
    arb_nonMonetaryBase: "90000",
    arb_nonMonetaryAdvalorem: "210000",
    arb_slabs: [makeDefaultSlab("arb")],

    neg_claimantBasePercent: "1",
    neg_respondentBasePercent: "0",
    neg_claimantSuccessPercent: "0.5",
    neg_respondentSuccessPercent: "0.5",
    neg_slabs: [makeDefaultSlab("neg")],
  };
}

const pf = (v) => parseFloat(v) || 0;

function buildPayload(values, activeTab) {
  const getSlabs = (slabs, type) =>
    slabs.map((s) => {
      const obj = {};
      slabConfig[type].cols.forEach((col) => {
        obj[col] = pf(s[col]);
      });
      return obj;
    });

  const payload = {
    partyCode: values.partyCode,
    mode: values.mode,
    CurrencyUnit: values.CurrencyUnit,
  };

  if (activeTab === "med") {
    const medSlabs = getSlabs(values.med_slabs, "med");
    payload.mediation =
      medSlabs.length === 0
        ? null
        : {
            claimantShare: pf(values.med_claimantShare),
            respondentShare: pf(values.med_respondentShare),
            adValoremUpfront: pf(values.med_adValoremUpfront),
            adValoremSuccess: pf(values.med_adValoremSuccess),
            serviceFee: pf(values.med_serviceFee),
            nonMonetaryBase: pf(values.med_nonMonetaryBase),
            nonMonetaryAdvalorem: pf(values.med_nonMonetaryAdvalorem),
            slabs: medSlabs,
          };
  }

  if (activeTab === "arb") {
    const arbSlabs = getSlabs(values.arb_slabs, "arb");
    payload.slabs = arbSlabs;
    // arbSlabs.length === 0
    //   ? null
    //   : {
    //       claimantPercent: pf(values.arb_claimantPercent),
    //       respondentPercent: pf(values.arb_respondentPercent),
    //       filingFee: pf(values.arb_filingFee),
    //       nonMonetaryBase: pf(values.arb_nonMonetaryBase),
    //       nonMonetaryAdvalorem: pf(values.arb_nonMonetaryAdvalorem),
    //       slabs: arbSlabs,
    //     };
  }

  if (activeTab === "neg") {
    const negSlabs = getSlabs(values.neg_slabs, "neg");
    payload.negotiation =
      negSlabs.length === 0
        ? null
        : {
            claimantBasePercent: pf(values.neg_claimantBasePercent),
            respondentBasePercent: pf(values.neg_respondentBasePercent),
            claimantSuccessPercent: pf(values.neg_claimantSuccessPercent),
            respondentSuccessPercent: pf(values.neg_respondentSuccessPercent),
            slabs: negSlabs,
          };
  }

  return payload;
}

const SlabInputCell = memo(({ name, value, onChange, onBlur, placeholder }) => (
  <CustomInputField
    className="input-white"
    variant="outlined"
    name={name}
    value={value}
    placeholder={placeholder}
    onChange={onChange}
    onBlur={onBlur}
    type="number"
    inputProps={{ step: "any" }}
  />
));

function GlobalSettings({ type, fields, values, handleChange, handleBlur }) {
  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitleGroup>
          <SectionTitle>Global settings</SectionTitle>
          <SectionSubtitle>
            Rates and fees applied across every slab in this tab
          </SectionSubtitle>
        </SectionTitleGroup>
      </SectionHeader>
      <SectionBody>
        <GlobalsGrid>
          {fields.map(({ label, name, step, min, max }) => (
            <FieldWrapper key={name}>
              <FieldLabel>{label}</FieldLabel>
              <CustomInputField
                className="input-white"
                variant="outlined"
                name={name}
                value={values[name]}
                onChange={handleChange}
                onBlur={handleBlur}
                type="number"
                inputProps={{ step: step || "any", min, max }}
              />
            </FieldWrapper>
          ))}
        </GlobalsGrid>
      </SectionBody>
    </SectionCard>
  );
}

function SlabSection({
  type,
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  enqueueSnackbar,
}) {
  const cfg = slabConfig[type];
  const slabsKey = `${type}_slabs`;
  const slabs = values[slabsKey];

  const addSlab = useCallback(() => {
    if (slabs.length >= cfg.max) {
      enqueueSnackbar(`Max ${cfg.max} slabs allowed`, { variant: "error" });
      return;
    }
    setFieldValue(slabsKey, [...slabs, makeDefaultSlab(type)]);
  }, [slabs, cfg.max, slabsKey, type, setFieldValue, enqueueSnackbar]);

  const clearSlabs = useCallback(() => {
    setFieldValue(slabsKey, []);
  }, [slabsKey, setFieldValue]);

  const deleteSlab = useCallback(
    (id) => {
      setFieldValue(
        slabsKey,
        slabs.filter((s) => s.id !== id),
      );
    },
    [slabs, slabsKey, setFieldValue],
  );

  const atMax = slabs.length >= cfg.max;

  return (
    <SectionCard>
      <SectionHeader>
        <SectionTitleGroup>
          <SectionTitle>Slabs</SectionTitle>
          <SectionSubtitle>Up to {cfg.max} slabs for this tab</SectionSubtitle>
        </SectionTitleGroup>
        <Badge $warn={atMax}>
          {slabs.length} / {cfg.max}
        </Badge>
      </SectionHeader>
      <SectionBody>
        <SlabHeaderRow>
          <ButtonGroup>
            <SecondaryCTAButton
              type="button"
              onClick={addSlab}
              disabled={atMax}
              style={{ paddingLeft: 0 }}
            >
              + Add slab
            </SecondaryCTAButton>
            <DangerTextButton
              type="button"
              onClick={clearSlabs}
              disabled={slabs.length === 0}
            >
              Clear all
            </DangerTextButton>
          </ButtonGroup>
        </SlabHeaderRow>
        <TableWrapper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={headCellStyle}>#</TableCell>
                {cfg.labels.map((lbl) => (
                  <TableCell key={lbl} style={headCellStyle}>
                    {lbl}
                  </TableCell>
                ))}
                <TableCell style={{ width: 40, ...headCellStyle }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {slabs.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={cfg.labels.length + 2}
                    style={{ border: "none", padding: 0 }}
                  >
                    <EmptyState>
                      No slabs yet — add one to start this pricing table.
                    </EmptyState>
                  </TableCell>
                </TableRow>
              )}
              {slabs.map((slab, idx) => (
                <StyledTableRow key={slab.id}>
                  <TableCell>
                    <RowNum>{String(idx + 1).padStart(2, "0")}</RowNum>
                  </TableCell>
                  {cfg.cols.map((col, i) => (
                    <TableCell key={col} style={{ position: "relative" }}>
                      <SlabInputCell
                        name={`${slabsKey}[${idx}].${col}`}
                        value={slab[col]}
                        placeholder={cfg.placeholders[i]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </TableCell>
                  ))}
                  <TableCell>
                    {slabs.length > 1 && (
                      <RemoveButton
                        type="button"
                        onClick={() => deleteSlab(slab.id)}
                        aria-label={`Remove slab ${idx + 1}`}
                        title="Remove slab"
                      >
                        <DeleteIcon
                          src={require("../../../assets/images/removeIcon.svg")}
                          alt=""
                        />
                      </RemoveButton>
                    )}
                  </TableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        </TableWrapper>
      </SectionBody>
    </SectionCard>
  );
}

const headCellStyle = {
  fontFamily: theme.fonts.primaryFontSemiBold,
  fontSize: 10.5,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: MUTE,
  paddingBottom: 10,
  paddingTop: 0,
  whiteSpace: "nowrap",
  borderBottom: `1px solid ${LINE}`,
};

const PricingUpload = () => {
  const [activeTab, setActiveTab] = useState("arb");
  const { setLoader } = useLoader();
  const { enqueueSnackbar } = useSnackbar();

  async function onFormSubmit(values, { setSubmitting }) {
    if (!values.partyCode) {
      enqueueSnackbar("Please select a party code", { variant: "error" });
      setSubmitting(false);
      return;
    }

    const currentSlabs = values[`${activeTab}_slabs`];
    if (!currentSlabs || currentSlabs.length === 0) {
      enqueueSnackbar("Add at least one slab", { variant: "error" });
      setSubmitting(false);
      return;
    }

    const payload = buildPayload(values, activeTab);

    try {
      setLoader({ state: true, message: "Uploading Pricing..." });
      const response = await CaseService.uploadPricing(payload);
      if (response) {
        enqueueSnackbar("Pricing uploaded successfully", {
          variant: "success",
        });
      }
    } catch (e) {
      const message = getErrorMessage(e);
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setLoader({ state: false });
      setSubmitting(false);
    }
  }

  const medFields = [
    { label: "Claimant Share (0–1)", name: "med_claimantShare", step: "0.01" },
    {
      label: "Respondent Share (0–1)",
      name: "med_respondentShare",
      step: "0.01",
    },
    {
      label: "Ad Valorem Upfront (0–1)",
      name: "med_adValoremUpfront",
      step: "0.01",
    },
    {
      label: "Ad Valorem Success (0–1)",
      name: "med_adValoremSuccess",
      step: "0.01",
    },
    { label: "Service Fee", name: "med_serviceFee" },
    { label: "Non-monetary Base", name: "med_nonMonetaryBase" },
    { label: "Non-monetary Ad-valorem", name: "med_nonMonetaryAdvalorem" },
  ];

  // const arbFields = [
  //   { label: "Claimant %", name: "arb_claimantPercent", step: "0.01" },
  //   { label: "Respondent %", name: "arb_respondentPercent", step: "0.01" },
  //   { label: "Filing Fee", name: "arb_filingFee" },
  //   { label: "Non-monetary Base", name: "arb_nonMonetaryBase" },
  //   { label: "Non-monetary Ad-valorem", name: "arb_nonMonetaryAdvalorem" },
  // ];

  const negFields = [
    {
      label: "Claimant Base % (0–1)",
      name: "neg_claimantBasePercent",
      step: "0.01",
    },
    {
      label: "Respondent Base % (0–1)",
      name: "neg_respondentBasePercent",
      step: "0.01",
    },
    {
      label: "Claimant Success % (0–1)",
      name: "neg_claimantSuccessPercent",
      step: "0.01",
    },
    {
      label: "Respondent Success % (0–1)",
      name: "neg_respondentSuccessPercent",
      step: "0.01",
    },
  ];

  const activeTabLabel = TAB_LIST.find((t) => t.key === activeTab)?.label;

  return (
    <OuterContainer>
      <ActionBar breadcrumbs={["Pricing Upload"]} />
      <Container>
        <PageHeader>
          <PageSubtitle>
            Configure fee slabs for arbitration per party code.
          </PageSubtitle>
        </PageHeader>

        <Formik
          initialValues={buildInitialValues()}
          onSubmit={onFormSubmit}
          enableReinitialize
          validateOnBlur
          validateOnChange
          validateOnMount
        >
          {({
            values,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
            errors,
            touched,
          }) => (
            <FormContainer onSubmit={handleSubmit}>
              <ConfigBar>
                <PartyCodeSelect
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  values={values}
                />

                <ModeGroup>
                  <FieldLabel>Mode</FieldLabel>
                  <ModeTrack>
                    {["append", "replace"].map((m) => (
                      <ModeChip
                        key={m}
                        type="button"
                        active={values.mode === m}
                        onClick={() => setFieldValue("mode", m)}
                      >
                        {m}
                      </ModeChip>
                    ))}
                  </ModeTrack>
                  <ModeHint>
                    {values.mode === "append"
                      ? "New slabs are added to the existing table."
                      : "Existing slabs for this tab are replaced entirely."}
                  </ModeHint>
                </ModeGroup>

                <FieldWrapper style={{ minWidth: 160 }}>
                  <FieldLabel>Currency Unit</FieldLabel>
                  <CustomInputField
                    className="input-white"
                    variant="outlined"
                    name="CurrencyUnit"
                    value={values.CurrencyUnit}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="INR"
                  />
                </FieldWrapper>
              </ConfigBar>

              <FilterTabsWrapper>
                {TAB_LIST.map(({ key, label }) => (
                  <FilterTab
                    key={key}
                    type="button"
                    active={activeTab === key}
                    onClick={() => setActiveTab(key)}
                  >
                    {label}
                  </FilterTab>
                ))}
              </FilterTabsWrapper>

              {/* Negotiation Panel */}
              {activeTab === "neg" && (
                <>
                  <GlobalSettings
                    type="neg"
                    fields={negFields}
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                  <SlabSection
                    type="neg"
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    enqueueSnackbar={enqueueSnackbar}
                  />
                </>
              )}

              {/* Mediation Panel */}
              {activeTab === "med" && (
                <>
                  <GlobalSettings
                    type="med"
                    fields={medFields}
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  />
                  <SlabSection
                    type="med"
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    enqueueSnackbar={enqueueSnackbar}
                  />
                </>
              )}

              {/* Arbitration Panel */}
              {activeTab === "arb" && (
                <>
                  {/* <GlobalSettings
                    type="arb"
                    fields={arbFields}
                    values={values}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                  /> */}
                  <SlabSection
                    type="arb"
                    values={values}
                    setFieldValue={setFieldValue}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    enqueueSnackbar={enqueueSnackbar}
                  />
                </>
              )}

              <SubmitBar>
                <SubmitContext>
                  Uploading <strong>{activeTabLabel}</strong> pricing
                  {values.partyCode ? (
                    <>
                      {" "}
                      for <strong>{values.partyCode}</strong>
                    </>
                  ) : null}
                </SubmitContext>
                <PrimaryCTAButton
                  style={{ width: "auto" }}
                  type="submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Uploading…" : "Upload pricing"}
                </PrimaryCTAButton>
              </SubmitBar>
            </FormContainer>
          )}
        </Formik>
      </Container>
    </OuterContainer>
  );
};

export default PricingUpload;

export const OuterContainer = styled.div`
  border-radius: 10px;
  background-color: ${SURFACE};
  margin: 40px 15px;
  padding-top: 1px;
  @media ${theme?.breakpoints?.sm_up} {
    margin: 0;
    padding-top: 0;
    border-radius: 0;
    background-color: ${SURFACE};
  }
`;

export const Container = styled.div`
  padding: 20px 15px 48px;
  display: flex;
  flex-direction: column;
  width: 90%;
  @media ${theme?.breakpoints?.sm_up} {
    padding: 32px 43px 56px;
  }
`;

export const PageHeader = styled.div`
  margin-bottom: 28px;
`;

export const PageTitle = styled.h1`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 22px;
  color: ${COLORS.COLOR_DARK};
  margin: 0 0 6px;
  letter-spacing: -0.01em;
`;

export const PageSubtitle = styled.p`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13.5px;
  color: ${INK_SOFT};
  margin: 0;
  line-height: 1.5;
`;

export const FormContainer = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const ConfigBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  align-items: flex-start;
  background: #ffffff;
  border: 1px solid ${LINE};
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
`;

export const TopRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  max-width: 340px;
  flex: 1 1 260px;
`;

export const ModeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ModeGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1 1 220px;
`;

export const ModeTrack = styled.div`
  display: inline-flex;
  padding: 3px;
  background: ${SURFACE};
  border: 1px solid ${LINE};
  border-radius: 8px;
  width: fit-content;
`;

export const ModeHint = styled.p`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11.5px;
  color: ${MUTE};
  margin: 0;
  line-height: 1.4;
`;

export const ModeChip = styled.button`
  padding: 6px 16px;
  border-radius: 6px;
  border: none;
  background-color: ${({ active }) =>
    active ? COLORS.BTN_GREEN : "transparent"};
  color: ${({ active }) => (active ? "#ffffff" : INK_SOFT)};
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12px;
  cursor: pointer;
  text-transform: capitalize;
  transition: all 0.15s ease;
`;

export const FilterTabsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 20px;
  border-radius: 10px;
  background: #ffffff;
  border: 1px solid ${LINE};
  padding: 4px;
  width: fit-content;
`;

export const FilterTab = styled.button`
  padding: 9px 22px;
  border: none;
  border-radius: 7px;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 12.5px;
  cursor: pointer;
  transition: all 0.15s ease;
  background-color: ${({ active }) => (active ? TEAL : "transparent")};
  color: ${({ active }) => (active ? "#ffffff" : INK_SOFT)};
  letter-spacing: 0.2px;

  &:hover {
    background-color: ${({ active }) => (active ? TEAL_DARK : TEAL_WASH)};
    color: ${({ active }) => (active ? "#ffffff" : TEAL_DARK)};
  }
`;

export const SectionCard = styled.div`
  background-color: #ffffff;
  border-radius: 10px;
  margin-bottom: 16px;
  overflow: hidden;
  border: 1px solid ${LINE};
`;

export const SectionHeader = styled.div`
  padding: 16px 20px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  border-bottom: 1px solid ${LINE};
`;

export const SectionTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SectionTitle = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 13.5px;
  color: ${INK};
`;

export const SectionSubtitle = styled.span`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11.5px;
  color: ${MUTE};
`;

export const SectionBody = styled.div`
  padding: 20px;
`;

export const GlobalsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 16px;
`;

export const FieldWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FieldLabel = styled.label`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10.5px;
  color: ${MUTE};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const SlabHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 14px;
`;

export const SlabCountLabel = styled.h4`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 11px;
  color: ${MUTE};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

export const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DangerTextButton = styled.button`
  background: transparent;
  border: 1px solid ${LINE};
  color: ${INK_SOFT};
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  padding: 6px 14px;
  border-radius: 7px;
  cursor: pointer;
  transition: all 0.12s ease;

  &:hover:not(:disabled) {
    border-color: ${DANGER};
    color: ${DANGER};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

export const TableWrapper = styled.div`
  overflow-x: auto;
`;

export const StyledTableRow = styled(TableRow)`
  border-bottom: 1px solid ${LINE};
  transition: background-color 0.12s ease;

  &:hover {
    background-color: ${SURFACE};
  }

  &:last-child {
    border-bottom: none;
  }
`;

export const RowNum = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  color: ${MUTE};
  font-variant-numeric: tabular-nums;
`;

export const Badge = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 11px;
  font-variant-numeric: tabular-nums;
  background: ${({ $warn }) => ($warn ? "rgba(220, 38, 38, 0.1)" : TEAL_WASH)};
  color: ${({ $warn }) => ($warn ? DANGER : TEAL_DARK)};
  border-radius: 6px;
  padding: 4px 10px;
  white-space: nowrap;
`;

export const EmptyState = styled.div`
  padding: 28px 4px;
  text-align: center;
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12.5px;
  color: ${MUTE};
`;

export const SubmitBar = styled.div`
  position: sticky;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #ffffff;
  border: 1px solid ${LINE};
  border-radius: 10px;
  padding: 14px 20px;
  margin-top: 8px;
  box-shadow: 0 -4px 16px rgba(15, 23, 42, 0.06);
`;

export const SubmitContext = styled.span`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12.5px;
  color: ${INK_SOFT};

  strong {
    font-family: ${theme.fonts.primaryFontSemiBold};
    color: ${INK};
  }
`;

export const FormControl = styled.div`
  margin-bottom: 13px;
  @media ${theme?.breakpoints?.sm_up} {
    width: 436px;
  }
`;

export const Row = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 25px;
`;

export const RemoveButton = styled.button`
  border: none;
  background: transparent;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.12s ease;

  &:hover {
    background-color: rgba(220, 38, 38, 0.08);
  }
`;

export const DeleteIcon = styled.img`
  width: 18px;
  height: 18px;
  object-fit: contain;
`;

export const SelectWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const SelectLabel = styled(FormLabel)`
  display: flex;
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 10.5px;
  color: ${MUTE};
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const RequiredStar = styled.span`
  color: ${DANGER};
  margin-left: 4px;
`;

export const PlaceholderText = styled.span`
  color: ${MUTE};
  font-family: ${theme.fonts.primaryFontRegular};
`;

export const StyledAutocomplete = styled(Autocomplete)`
  .MuiOutlinedInput-root {
    border-radius: 8px;
    transition: all 0.3s ease;
    background: white;
    &:hover {
      border-color: #cbd5e1;
    }
    &.Mui-focused {
      box-shadow: 0 0 0 3px ${TEAL_WASH};
    }
  }
  .MuiInputBase-input {
    font-size: 15px !important;
    font-weight: 500 !important;
    color: ${INK} !important;
    &::placeholder {
      color: ${MUTE};
      opacity: 1;
    }
  }
`;

export const StyledSelect = styled(Select)`
  width: 100% !important;
  min-width: 260px;
  border-radius: 8px !important;
  font-family: ${theme.fonts.primaryFontRegular} !important;

  &.Mui-focused {
    box-shadow: 0 0 0 3px ${TEAL_WASH} !important;
  }

  .MuiOutlinedInput-input {
    font-size: 14.5px !important;
    color: ${INK} !important;
    padding: 11px 14px !important;
  }

  .MuiOutlinedInput-notchedOutline {
    border-color: ${LINE} !important;
  }

  &:hover .MuiOutlinedInput-notchedOutline {
    border-color: #cbd5e1 !important;
  }

  &.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: ${TEAL} !important;
  }
`;

export const PartyCodeItem = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

export const CodeLabel = styled.span`
  font-family: ${theme.fonts.primaryFontSemiBold};
  font-size: 14px;
  color: ${INK};
  font-weight: 600;
`;

export const CodeDetail = styled.span`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: ${MUTE};
  margin-top: 2px;
`;

export const ErrorText = styled.span`
  font-size: 11.5px;
  color: ${DANGER};
  display: block;
  font-family: ${theme.fonts.primaryFontRegular};
`;

export const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 4px 0;
`;

export const OptionContent = styled.div`
  flex: 1;
`;

export const OptionName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  font-family: openSans-SemiBold, sans-serif;
  margin-bottom: 4px;
`;

export const OptionEmail = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${MUTE};
  font-family: openSans-SemiBold, sans-serif;
`;

export const OptionCheck = styled.span`
  font-size: 16px;
  color: ${TEAL};
  opacity: 0;
  transition: opacity 0.2s ease;
  li[data-option-index]:hover & {
    opacity: 1;
  }
`;
